import { Injectable, InputSignal, Type } from '@angular/core';
import { RecommendationRenderInfo } from '../../../data/models/recommendations.models';

/**
 * Interface that all recommendation card plugin components must satisfy.
 * Card plugins are displayed in the body of recommendation cards.
 */
export interface RecommendationCardPlugin {
    renderInfo: InputSignal<RecommendationRenderInfo>;
}

/**
 * Interface that all recommendation dialog plugin components must satisfy.
 * Dialog plugins are displayed when a user clicks on a recommendation card.
 */
export interface RecommendationDetailsDialogPlugin {
    renderInfo: InputSignal<RecommendationRenderInfo>;
}

@Injectable({
    providedIn: 'root',
})
export class RecommendationComponentRegistryService {
    static readonly cardPluginRegistry = new Map<string, Type<RecommendationCardPlugin>>();
    static readonly dialogPluginRegistry = new Map<string, Type<RecommendationDetailsDialogPlugin>>();

    getCardPlugin(name: string): Type<RecommendationCardPlugin> | undefined {
        return RecommendationComponentRegistryService.cardPluginRegistry.get(name);
    }

    getDialogPlugin(name: string): Type<RecommendationDetailsDialogPlugin> | undefined {
        return RecommendationComponentRegistryService.dialogPluginRegistry.get(name);
    }
}

/**
 * Registers a card plugin under an explicit string key. The key must match the value passed
 * as `cardPluginComponentName` on the corresponding RenderInfo. An explicit string is required
 * because class names are mangled in production builds, which would otherwise cause collisions.
 */
export function registerAsCardPlugin(name: string, plugin: Type<RecommendationCardPlugin>): void {
    RecommendationComponentRegistryService.cardPluginRegistry.set(name, plugin);
}

/**
 * Registers a dialog plugin under an explicit string key. The key must match the value passed
 * as `dialogPluginComponentName` on the corresponding RenderInfo. An explicit string is required
 * because class names are mangled in production builds, which would otherwise cause collisions.
 */
export function registerAsDialogPlugin(name: string, plugin: Type<RecommendationDetailsDialogPlugin>): void {
    RecommendationComponentRegistryService.dialogPluginRegistry.set(name, plugin);
}
